<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import Datepicker from '@vuepic/vue-datepicker'
import '@vuepic/vue-datepicker/dist/main.css'
import Bs2Title from '@/components/common/layout/Bs2Title.vue'
import TextInput from '@/components/common/inputs/TextInput.vue'
import MarkdownWysiwygInput from '@/components/common/inputs/MarkdownWysiwygInput.vue'
import {
  useNewsAdminStore,
  type NewsItem,
  type NewsDetail,
  type NewsType,
  type NewsStatus,
  type PublishNewsValidationError,
} from '@/stores/useNewsAdminStore'
import { NEWS_TYPE_BADGES, NEWS_TYPE_LABELS } from '@/utils/newsDisplay'
import {
  ArchiveBoxIcon,
  ArrowUturnLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  NewspaperIcon,
  PencilSquareIcon,
  PlusIcon,
  XCircleIcon,
} from '@heroicons/vue/24/outline'

const store = useNewsAdminStore()

// ─── Édition (création + update) ────────────────────────────────────────────
const showEditModal = ref(false)
const editingDetail = ref<NewsDetail | null>(null) // null en création
const editing = ref(false)
const editError = ref<string | null>(null)

interface EditForm {
  uuid?: string
  type: NewsType
  title: string
  body: string
}

const form = ref<EditForm>(buildEmptyForm())

function buildEmptyForm(): EditForm {
  return {
    type: 'INFO',
    title: '',
    body: '',
  }
}

function detailToForm(d: NewsDetail): EditForm {
  return {
    uuid: d.uuid,
    type: d.type,
    title: d.title,
    body: d.body,
  }
}

function openCreate() {
  editingDetail.value = null
  form.value = buildEmptyForm()
  editError.value = null
  showEditModal.value = true
}

async function openEdit(item: NewsItem) {
  editingDetail.value = null
  editError.value = null
  showEditModal.value = true
  const detail = await store.fetchDetail(item.uuid)
  if (detail) {
    editingDetail.value = detail
    form.value = detailToForm(detail)
  } else {
    editError.value = 'Impossible de charger le détail'
  }
}

function closeEdit() {
  showEditModal.value = false
  editingDetail.value = null
  editError.value = null
}

async function saveEdit() {
  editing.value = true
  editError.value = null
  try {
    if (!form.value.title.trim() || !form.value.body.trim()) {
      editError.value = 'Le titre et le contenu sont obligatoires.'
      return
    }
    const payload = {
      type: form.value.type,
      title: form.value.title.trim(),
      body: form.value.body.trim(),
    }
    const res = editingDetail.value
      ? await store.update(editingDetail.value.uuid, payload)
      : await store.create(payload)
    if (res.ok) {
      closeEdit()
    } else {
      editError.value = res.message
    }
  } finally {
    editing.value = false
  }
}

// ─── Publication (immédiate ou programmée) ──────────────────────────────────
const showPublishModal = ref(false)
const publishTarget = ref<NewsItem | null>(null)
const publishMode = ref<'now' | 'scheduled'>('now')
const scheduledDate = ref<Date | null>(null)
const publishing = ref(false)
const publishErrors = ref<PublishNewsValidationError[]>([])
const showPublishErrorsModal = ref(false)

const minScheduleDate = computed(() => new Date())

function openPublish(item: NewsItem) {
  publishTarget.value = item
  publishMode.value = 'now'
  scheduledDate.value = null
  showPublishModal.value = true
}

function closePublish() {
  showPublishModal.value = false
  publishTarget.value = null
}

async function confirmPublish() {
  if (!publishTarget.value) return
  if (publishMode.value === 'scheduled' && !scheduledDate.value) {
    alert('Choisissez une date de publication.')
    return
  }
  publishing.value = true
  try {
    const publishedAt =
      publishMode.value === 'scheduled' && scheduledDate.value
        ? scheduledDate.value.toISOString()
        : undefined
    const res = await store.publish(publishTarget.value.uuid, publishedAt)
    if (res.ok) {
      if (res.data.ok) {
        closePublish()
      } else {
        publishErrors.value = res.data.errors ?? []
        showPublishErrorsModal.value = true
      }
    } else {
      alert(res.message)
    }
  } finally {
    publishing.value = false
  }
}

async function unpublish(item: NewsItem) {
  if (!confirm('Repasser cette news en brouillon ? Elle disparaîtra du fil d\'actualité.')) return
  const res = await store.unpublish(item.uuid)
  if (!res.ok) alert(res.message)
}

async function archive(item: NewsItem) {
  if (!confirm('Archiver cette news ? Elle ne sera plus visible dans le fil d\'actualité.')) return
  const res = await store.archive(item.uuid)
  if (!res.ok) alert(res.message)
}

// ─── Affichage ──────────────────────────────────────────────────────────────
function statusBadgeClass(s: NewsStatus) {
  if (s === 'PUBLISHED') return 'badge-success'
  if (s === 'ARCHIVED') return 'badge-ghost'
  return 'badge-warning'
}

function isScheduled(item: NewsItem): boolean {
  return item.status === 'PUBLISHED' && !!item.publishedAt && new Date(item.publishedAt) > new Date()
}

function formatDateTime(iso: string | null): string {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

onMounted(async () => {
  await store.fetchAll()
})
</script>

<template>
  <div class="p-6">
    <Bs2Title
      title="News & changelog"
      subtitle="Actualités poussées aux utilisateurs : fil d'actualité, modal nouveautés, notification temps réel"
    />

    <div class="flex flex-wrap gap-2 mt-4">
      <router-link to="/dashboard/admin" class="btn btn-outline btn-sm">← Retour dashboard admin</router-link>
      <button class="btn btn-primary btn-sm gap-1" @click="openCreate">
        <PlusIcon class="w-4 h-4" />
        Nouvelle news
      </button>
    </div>

    <div class="mt-6 card bg-base-100 shadow">
      <div class="card-body">
        <div v-if="store.isLoading" class="py-8 text-center">
          <span class="loading loading-spinner loading-lg" />
        </div>
        <div v-else-if="store.items.length === 0" class="py-8 text-center text-base-content/60">
          Aucune news. Créez la première avec le bouton "Nouvelle news".
        </div>
        <div v-else class="overflow-x-auto">
          <table class="table table-sm">
            <thead>
              <tr>
                <th>Statut</th>
                <th>Type</th>
                <th>Titre</th>
                <th>Publication</th>
                <th class="text-center">Vues</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in store.items" :key="item.uuid" class="hover">
                <td>
                  <span class="badge badge-sm" :class="statusBadgeClass(item.status)">{{ item.status }}</span>
                </td>
                <td>
                  <span class="badge badge-sm badge-outline" :class="NEWS_TYPE_BADGES[item.type]">
                    {{ NEWS_TYPE_LABELS[item.type] }}
                  </span>
                </td>
                <td class="max-w-md">
                  <div class="text-sm truncate" :title="item.title">{{ item.title }}</div>
                </td>
                <td class="text-sm whitespace-nowrap">
                  {{ formatDateTime(item.publishedAt) }}
                  <span v-if="isScheduled(item)" class="badge badge-warning badge-xs ml-1">programmée</span>
                </td>
                <td class="text-center tabular-nums">{{ item.readCount }}</td>
                <td>
                  <div class="flex gap-1">
                    <button class="btn btn-ghost btn-xs" @click="openEdit(item)" title="Éditer">
                      <PencilSquareIcon class="w-3 h-3" />
                    </button>
                    <button
                      v-if="item.status !== 'PUBLISHED'"
                      class="btn btn-ghost btn-xs text-success"
                      @click="openPublish(item)"
                      title="Publier"
                    >
                      <CheckCircleIcon class="w-3 h-3" />
                    </button>
                    <button
                      v-if="item.status === 'PUBLISHED'"
                      class="btn btn-ghost btn-xs"
                      @click="unpublish(item)"
                      title="Repasser en brouillon"
                    >
                      <ArrowUturnLeftIcon class="w-3 h-3" />
                    </button>
                    <button
                      v-if="item.status !== 'ARCHIVED'"
                      class="btn btn-ghost btn-xs"
                      @click="archive(item)"
                      title="Archiver"
                    >
                      <ArchiveBoxIcon class="w-3 h-3" />
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Modal Création/Édition -->
    <div class="modal" :class="{ 'modal-open': showEditModal }">
      <div class="modal-box max-w-5xl">
        <h3 class="font-bold text-lg flex items-center gap-2">
          <NewspaperIcon class="w-6 h-6" />
          {{ editingDetail ? `Édition — ${form.title}` : 'Nouvelle news' }}
        </h3>

        <div class="mt-4 space-y-4">
          <div class="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div class="form-control">
              <label class="label py-1"><span class="label-text text-xs">Type</span></label>
              <select v-model="form.type" class="select select-bordered select-sm" :disabled="editing">
                <option value="INFO">Information</option>
                <option value="FEATURE">Nouveauté</option>
                <option value="CHANGELOG">Changelog</option>
                <option value="ALERT">Alerte</option>
              </select>
            </div>
          </div>

          <TextInput v-model="form.title" label="Titre" required :disabled="editing" />
          <MarkdownWysiwygInput
            name="body"
            v-model="form.body"
            label="Contenu"
            required
            :disabled="editing"
          />

          <div v-if="editError" class="alert alert-error text-sm">{{ editError }}</div>
        </div>

        <div class="modal-action">
          <button class="btn" @click="closeEdit" :disabled="editing">Annuler</button>
          <button class="btn btn-primary" @click="saveEdit" :disabled="editing">
            {{ editing ? 'Enregistrement…' : 'Enregistrer' }}
          </button>
        </div>
      </div>
      <div class="modal-backdrop" @click="closeEdit" />
    </div>

    <!-- Modal Publication -->
    <div class="modal" :class="{ 'modal-open': showPublishModal }">
      <div class="modal-box max-w-xl">
        <h3 class="font-bold text-lg flex items-center gap-2">
          <CheckCircleIcon class="w-6 h-6 text-success" />
          Publier — {{ publishTarget?.title }}
        </h3>

        <div class="mt-4 space-y-3">
          <label class="flex items-center gap-3 cursor-pointer">
            <input v-model="publishMode" type="radio" value="now" class="radio radio-sm radio-primary" />
            <div>
              <div class="font-medium text-sm">Publier maintenant</div>
              <div class="text-xs text-base-content/60">
                Visible immédiatement, notification temps réel envoyée aux utilisateurs connectés.
              </div>
            </div>
          </label>

          <label class="flex items-center gap-3 cursor-pointer">
            <input v-model="publishMode" type="radio" value="scheduled" class="radio radio-sm radio-primary" />
            <div>
              <div class="font-medium text-sm">Programmer la publication</div>
              <div class="text-xs text-base-content/60">
                La news apparaîtra dans le fil et sera notifiée à la date choisie.
              </div>
            </div>
          </label>

          <div v-if="publishMode === 'scheduled'" class="pl-8">
            <Datepicker
              v-model="scheduledDate"
              locale="fr"
              format="dd/MM/yyyy HH:mm"
              :enable-time-picker="true"
              :min-date="minScheduleDate"
              auto-apply
              teleport="body"
              placeholder="Choisir la date et l'heure"
            />
          </div>
        </div>

        <div class="modal-action">
          <button class="btn" @click="closePublish" :disabled="publishing">Annuler</button>
          <button class="btn btn-primary" @click="confirmPublish" :disabled="publishing">
            {{ publishing ? 'Publication…' : publishMode === 'now' ? 'Publier' : 'Programmer' }}
          </button>
        </div>
      </div>
      <div class="modal-backdrop" @click="closePublish" />
    </div>

    <!-- Modal erreurs de publication -->
    <div class="modal" :class="{ 'modal-open': showPublishErrorsModal }">
      <div class="modal-box max-w-xl">
        <h3 class="font-bold text-lg flex items-center gap-2">
          <XCircleIcon class="w-6 h-6 text-error" />
          Publication impossible
        </h3>
        <p class="mt-3 text-sm">La news ne respecte pas les règles de publication :</p>
        <ul class="mt-3 space-y-2">
          <li v-for="(err, i) in publishErrors" :key="i" class="alert alert-warning text-sm">
            <ExclamationTriangleIcon class="w-4 h-4 shrink-0" />
            <div>
              <div class="font-mono text-xs opacity-70">{{ err.code }}</div>
              <div>{{ err.message }}</div>
            </div>
          </li>
        </ul>
        <div class="modal-action">
          <button class="btn btn-primary" @click="showPublishErrorsModal = false">Fermer</button>
        </div>
      </div>
      <div class="modal-backdrop" @click="showPublishErrorsModal = false" />
    </div>
  </div>
</template>
